<script lang="ts" setup>
const { hasSubnav, routeNameWithoutLocale } = useNavigation()
const { isSpotlightOpened, toggleModal } = useOverlays()
const { settingsPages } = useSettings()
const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const billingStore = useBillingStore()
const teamsStore = useTeamsStore()
const userStore = useUserStore()

const popoverOpened = ref(false)

const navigationLinks = computed<UDashboardSidebarLink[]>(() => {
  const links: UDashboardSidebarLink[] = [
    {
      active: routeNameWithoutLocale.value === 'api-slug',
      icon: getIcon('api'),
      label: t('navigation.api'),
      to: localePath({ path: '/api' }),
    },
    {
      icon: getIcon('useCases'),
      label: t('navigation.useCases'),
      to: localePath('use-cases'),
    },
  ]

  if (!billingStore.isSubscriptionActive) {
    links.push({
      icon: getIcon('pricing'),
      label: t('navigation.pricing'),
      to: localePath('pricing'),
    })
  }

  links.push({
    icon: getIcon('companies'),
    label: t('navigation.companies'),
    to: localePath('companies'),
  })

  return links
})

watch(
  () => route.path,
  () => (popoverOpened.value = false),
)

const springStyle = useSpring(
  {
    borderBottom: 12,
    height: 0,
    opacity: 0,
  },
  { duration: 250 },
)

watch(
  hasSubnav,
  (value) => {
    if (value) {
      nextTick(() => {
        springStyle.apply(
          {
            borderBottom: 0,
            height: 48,
            opacity: 1,
          },
        )
      })
    }
    else {
      nextTick(() => {
        springStyle.apply(
          {
            borderBottom: 12,
            height: 0,
            opacity: 0,
          },
        )
      })
    }
  },
  { immediate: true },
)

defineShortcuts({
  meta_k: {
    handler: () => toggleModal({
      opened: !isSpotlightOpened.value,
      type: 'spotlight',
    }),
  },
})

function onOpenSpotlight() {
  toggleModal({
    opened: true,
    type: 'spotlight',
  })
}
</script>

<template>
  <header class="absolute top-5 z-[80] flex w-full select-none flex-col px-4 sm:px-6 lg:px-8">
    <UContainer
      class="header-height gradient-texture-blur ring-texture relative z-50 flex w-full items-center gap-4 rounded-t-xl"
      :style="{
        borderBottomLeftRadius: `${springStyle.values.borderBottom}px`,
        borderBottomRightRadius: `${springStyle.values.borderBottom}px`,
      }"
    >
      <div class="mr-2 flex flex-1 flex-shrink-0 items-center">
        <BaseLogo
          class="xs:block hidden"
          is-link
        />

        <BaseEmblem
          class="xs:hidden"
          is-link
        />
      </div>

      <div class="hidden lg:flex lg:items-center lg:gap-1 xl:gap-3">
        <UButton
          v-for="link of navigationLinks"
          :key="link.label"
          class="group/nav-link group !no-underline"
          :label="link.label"
          size="lg"
          :target="link.to?.toString().startsWith('http') ? '_blank' : undefined"
          :to="link.to"
          variant="link"
        >
          <span class="flex items-center gap-2">
            <UIcon
              v-if="link.icon"
              class="transition-base size-5 group-hover/nav-link:text-indigo-400 dark:group-hover/nav-link:text-indigo-500"
              :class="{ 'text-indigo-400 dark:text-indigo-500': route.path.startsWith(link.to as string) || link.active }"
              :name="link.icon"
            />

            <span
              class="transition-base text-sm text-gray-700 group-hover:text-black dark:text-gray-300 dark:group-hover:text-white"
              :class="{ '!text-black dark:!text-white': route.path.startsWith(link.to as string) || link.active }"
            >
              {{ link.label }}
            </span>
          </span>
        </UButton>
      </div>

      <div class="flex flex-1 items-center justify-end gap-2 md:gap-3">
        <UButton @click="onOpenSpotlight">
          <span class="flex items-center gap-2 text-xs md:text-sm">
            <UIcon
              class="size-4 md:hidden"
              :name="getIcon('search')"
            />

            {{ t('globals.menu') }}

            <span class="hidden items-center gap-1 md:flex">
              <UKbd>⌘</UKbd>

              <UKbd>K</UKbd>
            </span>
          </span>
        </UButton>

        <template v-if="!userStore.user">
          <UButton
            class="hidden md:flex"
            :icon="getIcon('login')"
            :label="t('globals.login')"
            :to="localePath('auth-login')"
            :ui="{ icon: { base: 'text-indigo-700 dark:text-indigo-300' } }"
          />

          <UButton
            class="md:hidden"
            :icon="getIcon('login')"
            :label="t('globals.login')"
            size="xs"
            :to="localePath('auth-login')"
            :ui="{ icon: { base: 'text-indigo-700 dark:text-indigo-300' } }"
          />
        </template>

        <UPopover
          v-else
          v-model:open="popoverOpened"
          :popper="{
            offsetDistance: 8,
            placement: 'bottom',
            strategy: 'absolute',
          }"
        >
          <UButton
            class="h-8 md:h-9"
            size="2xs"
          >
            <div class="flex items-center justify-center">
              <BaseCoin size="xs" />

              <span class="text-xs uppercase text-gray-700 dark:text-white">
                <ClientOnly>
                  {{ formatNumber(teamsStore.currentTeam?.credits || 0) }}

                  <template #fallback>
                    <USkeleton class="h-4 w-8" />
                  </template>
                </ClientOnly>
              </span>

              <UIcon
                class="transition-base ml-1 size-3.5 transform text-gray-700 dark:text-white"
                :class="{ 'rotate-180': popoverOpened }"
                :name="getIcon('chevronDown')"
              />
            </div>
          </UButton>

          <template #panel>
            <div class="min-w-[240px] select-none">
              <UHeaderPopoverLinks
                :links="settingsPages"
                :ui="{ label: 'font-normal' }"
              />
            </div>
          </template>
        </UPopover>
      </div>
    </UContainer>

    <UContainer
      id="subnav-teleport-target"
      class="ring-texture gradient-texture-opacity -mt-px w-full rounded-b-xl backdrop-blur-xl"
      :style="{
        height: `${springStyle.values.height}px`,
        opacity: `${springStyle.values.opacity}`,
      }"
    />
  </header>

  <LayoutSpotlight v-if="isSpotlightOpened" />
</template>

<style lang="postcss">
.header-height {
  height: var(--header-height);
}
</style>
